import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Placeholder for the external date formatting utility
const formatDateTime = (date) => new Date(date).toLocaleString();

/**
 * Core function to generate the PDF document object containing the rendered HTML.
 *
 * @param {object} printRef - React ref pointing to the container element to capture.
 * @param {string} storeID - ID of the store.
 * @param {Date} start - Start date of the report range.
 * @param {Date} end - End date of the report range.
 * @param {string} reportTitle - The title of the report (e.g., 'Clock-in Report').
 * @param {object} storeInfo - Object containing store details.
 * @returns {jsPDF} The finalized PDF document object.
 */
const generateReportPDF = async (printRef, storeID, start, end, reportTitle, storeInfo) => {
    if (!printRef?.current) {
        console.error("Print reference is missing.");
        return null;
    }

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const headerHeight = 35; // Space reserved for the programmed header
    const footerHeight = 15; // Space reserved for the programmed footer

    const username = "Generated by Admin";
    const dateTime = formatDateTime(new Date());

    // NOTE: This assumes the report component itself (printRef.current) 
    // does NOT contain the store details/header elements, which should be added by jsPDF.
    
    // --- 🟩 Convert the page content to image (Requires html2canvas) ---
    const canvas = await html2canvas(printRef.current, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");
    const imgProps = pdf.getImageProperties(imgData);

    const pdfWidth = pageWidth - (2 * margin);
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    let positionY = 0; // Tracks position on the source image
    let pageNum = 1;

    // --- Loop for multi-page rendering ---
    while (positionY < pdfHeight) {
        if (pageNum > 1) {
            pdf.addPage();
        }

        // Available content height on the PDF page (excluding header and footer space)
        const availablePageContentHeight = pageHeight - headerHeight - footerHeight;
        
        // Height of the slice to be added to the PDF page
        const sliceHeight = Math.min(availablePageContentHeight, pdfHeight - positionY);

        // Add the slice of the image to the current PDF page
        // 10mm margin from the left/right, and start 35mm down to reserve space for header/logo
        pdf.addImage(
            imgData, 
            "PNG", 
            margin, 
            headerHeight, // Start Y position on PDF
            pdfWidth, 
            sliceHeight, // Height of the drawn slice
            null, 
            'NONE', 
            0, 
            positionY, // Start Y position on SOURCE IMAGE
            pdfWidth, 
            sliceHeight,
            null, 
            null, 
            'slow'
        );

        // Update position for the next page
        positionY += sliceHeight;
        pageNum++;
    }

    // --- Add Header and Footer to ALL Pages ---
    const finalPageCount = pdf.getNumberOfPages();
    const storeDetails = {
        name: `${storeInfo.name ?? ' '} (${storeID})`,
        address: storeInfo.address ?? ' ',
        phone: storeInfo.phone ?? ' '
    };

    for (let i = 1; i <= finalPageCount; i++) {
        pdf.setPage(i);

        // --- Header Content (Drawn on every page) ---
        pdf.setFontSize(10);
        pdf.text(storeDetails.name, margin, margin);
        pdf.text(`From: ${new Date(start).toLocaleDateString()} To: ${new Date(end).toLocaleDateString()}`, pageWidth - margin, margin, { align: "right" });

        pdf.setDrawColor(150);
        pdf.setLineWidth(0.3);
        pdf.line(margin, margin + 1, pageWidth - margin, margin + 1);

        pdf.setFontSize(9);
        pdf.text(storeDetails.address, margin, margin + 5);
        pdf.text(`Phone: ${storeDetails.phone}`, margin, margin + 10);
        
        pdf.setFontSize(12);
        pdf.text(reportTitle, pageWidth / 2, margin + 15, { align: "center" }); // Use the dynamic title

        // --- Footer Content (Drawn on every page) ---
        pdf.setFontSize(8);
        pdf.setTextColor(120);
        pdf.text(`Page ${i} of ${finalPageCount}`, pageWidth / 2, pageHeight - margin + 5, { align: "center" });
        pdf.text(username, margin, pageHeight - margin + 5);
        pdf.text(dateTime, pageWidth - margin, pageHeight - margin + 5, { align: "right" });
    }

    return pdf;
};


// --- 🟧 View Function (Opens PDF in new tab) ---
export const handleViewPDF = async (printRef, storeID, start, end, reportTitle = 'Report Preview', storeInfo) => {
    const pdf = await generateReportPDF(printRef, storeID, start, end, reportTitle, storeInfo);
    if (pdf) {
        const pdfBlobUrl = pdf.output("bloburl");
        window.open(pdfBlobUrl, "_blank");
    }
};

// --- 💾 Download Function (Saves PDF to computer) ---
export const handleDownloadPDF = async (printRef, storeID, start, end, reportTitle = 'Report', storeInfo) => {
    const pdf = await generateReportPDF(printRef, storeID, start, end, reportTitle, storeInfo);
    if (pdf) {
        // Creates a filename based on the title and dates
        const filename = `${reportTitle.toLowerCase().replace(/ /g, '-')}-${new Date(start).toLocaleDateString()}_${new Date(end).toLocaleDateString()}.pdf`;
        pdf.save(filename);
    }
};